import { Outlet, useParams } from 'react-router-dom'
import { useNavigate } from 'react-router-dom'

import { Box, Divider, Icon, Text, Touchable } from '@factorialco/deprecated-design-system'
import { Delete, Upload } from '@factorialco/f0-react/icons/app'
import { TrainingEmployeesDocument } from 'generated/resources/graphql'
import {
  EmployeeClassesSidepanelCertificatesDocument,
  EmployeeClassesSidepanelCertificatesQuery,
  TrainingParticipantsDocument,
  TrainingParticipantsQuery,
} from 'generated/resources/graphql'
import { EmployeeClassesSidepanelDeleteCertificateDocument } from 'generated/resources/graphql'

import Sidepanel from 'modules/core/components/Sidepanel'
import { useUnsafeCallback } from 'modules/core/hooks/useCallback'
import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { MaterialItem, MaterialsList } from 'modules/trainings/components/MaterialsList'
import { SessionsDataTable } from 'modules/trainings/components/Revamp/EmployeeClassesSidepanel/SessionsDatatable'
import { TrainingMembershipStatusTag } from 'modules/trainings/components/Shared/TrainingMembershipStatusTag'
import { useTrainingClasses } from 'modules/trainings/hooks/TrainingClasses/useTrainingClasses'

const EmployeesSidepanel = () => {
  const { id, employeeId } = useParams<{
    id: string
    employeeId: string
  }>()
  const { data: trainingParticipantsData } = useQuery<
    TrainingParticipantsQuery,
    { trainingId: string }
  >(TrainingParticipantsDocument, {
    variables: { trainingId: id ?? '' },
  })
  const data = useUnsafeMemo(
    () =>
      trainingParticipantsData?.trainings?.trainingMembershipsConnection?.edges?.map(
        (edge) => edge.node
      ) ?? [],
    [trainingParticipantsData]
  )

  const { data: employeeDetail, loading: loadingEmployee } = useQuery(TrainingEmployeesDocument, {
    variables: { ids: [String(employeeId)] },
    skip: !employeeId,
  })

  const employee = employeeDetail?.employees.employeesConnection.nodes[0]

  const { navigation } = useNavigation()
  const navigate = useNavigate()

  const shouldSkipTrainingClasses = !employee?.access.id
  const {
    trainingClasses,
    trainingStatus,
    trainingMembershipId,
    loading: loadingClasses,
  } = useTrainingClasses({
    trainingId: String(id),
    employeeId: String(employeeId),
    accessId: String(employee?.access.id),
    skip: shouldSkipTrainingClasses,
  })
  const readonly = !trainingClasses[0]?.training?._policies?.edit

  const activeEmployeeIndex = useUnsafeMemo(
    () => data.findIndex((x) => x.employee?.id === employee?.id),
    [data, employee]
  )

  const previousEmployeeId = data[activeEmployeeIndex - 1]?.employee?.id
  const nextEmployeeId = data[activeEmployeeIndex + 1]?.employee?.id

  const navigateToEmployee = useUnsafeCallback(
    (newEmployeeId: string) => {
      navigate(
        navigation.trainings.list.details_revamp.view.participants.employees.fullPath({
          id: String(id),
          employeeId: String(newEmployeeId),
        })
      )
    },
    [navigate, id, navigation]
  )

  if (loadingEmployee || loadingClasses) return null

  return (
    <Sidepanel
      width="wideXL"
      open
      title={
        <Box flexDirection="row" justifyContent="center" alignItems="center" flexGrow>
          {previousEmployeeId && (
            <Touchable onClick={() => navigateToEmployee(previousEmployeeId)}>
              <Icon.Small icon="chevron-left" />
            </Touchable>
          )}

          <Box paddingX="s24">
            <Text.Normal weight="semibold">{employee?.fullName}</Text.Normal>
          </Box>

          {nextEmployeeId && (
            <Touchable onClick={() => navigateToEmployee(nextEmployeeId)}>
              <Icon.Small icon="chevron-right" />
            </Touchable>
          )}
        </Box>
      }
      onClose={() =>
        navigate(
          navigation.trainings.list.details_revamp.view.participants.fullPath({ id: String(id) })
        )
      }
    >
      <Box gap="s16">
        <Box gap="s24">
          {trainingStatus && <TrainingMembershipStatusTag status={trainingStatus} />}
          <Text size="400" weight="semibold">
            {employee?.fullName}
          </Text>
          <Text size="300" weight="normal">
            {i18n.t('trainings.trainings_revamp.employee_sidepanel.subtitle')}
          </Text>
          <Certificates
            trainingId={String(id)}
            employeeId={String(employeeId)}
            trainingMembershipId={String(trainingMembershipId)}
            readonly={readonly}
          />
        </Box>
        <Divider />
        <Box gap="s40">
          {trainingClasses.map((tr) => (
            <Box key={tr.id}>
              <SessionsDataTable
                accessId={String(employee?.access.id)}
                trainingClassId={String(tr.id)}
                trainingClassName={tr.name ?? ''}
              />
            </Box>
          ))}
        </Box>
      </Box>
      <Outlet />
    </Sidepanel>
  )
}

function Certificates({
  trainingId,
  employeeId,
  trainingMembershipId,
  readonly,
}: {
  trainingId: string
  employeeId: string
  trainingMembershipId: string
  readonly: boolean
}) {
  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const [deleteCertificate] = useMutation(EmployeeClassesSidepanelDeleteCertificateDocument, {
    extractorFn: (data) => data.trainingsMutations.deleteTrainingsTrainingMembershipFile,
    awaitRefetchQueries: true,
    refetchQueries: [EmployeeClassesSidepanelCertificatesDocument],
  })
  const source = useGraphqlDataCollectionSource(
    {
      document: EmployeeClassesSidepanelCertificatesDocument,
      mapData: (data: EmployeeClassesSidepanelCertificatesQuery): MaterialItem[] =>
        data.trainings.trainingMembershipFilesConnection.nodes.map((item) => ({
          id: String(item.id),
          filename: item.filename,
          title: item.filename,
          contentType: item.contentType,
          type: 'membership',
          size: item.size,
        })),
      mapVariables: () => ({
        trainingMembershipIds: [trainingMembershipId],
      }),
      mapPagination: (data: EmployeeClassesSidepanelCertificatesQuery) => ({
        total: data.trainings.trainingMembershipFilesConnection.totalCount ?? 0,
      }),
      primaryActions: () => ({
        onClick: () => {
          navigate(
            navigation.trainings.list.details_revamp.view.participants.employees.uploadCertificate.fullPath(
              {
                id: trainingId,
                employeeId: employeeId,
                trainingMembershipId: trainingMembershipId,
              }
            )
          )
        },
        label: i18n.t('common.upload'),
        icon: Upload,
      }),
      itemUrl: (item: MaterialItem) =>
        navigation.trainings.list.details_revamp.view.participants.previewCertificate.fullPath({
          id: trainingId,
          trainingMembershipId: trainingMembershipId,
          trainingMembershipFileId: String(item.id),
          employeeId: employeeId,
        }),
      itemActions: (item: MaterialItem) => {
        if (readonly) return []
        return [
          {
            onClick: async () => {
              const result = await deleteCertificate({ variables: { id: item.id } })
              handleResult(result, () => {
                app.setFlash({
                  type: 'wrong',
                  message: i18n.t('trainings.training_files.delete_error'),
                })
              })
            },
            label: i18n.t('common.delete'),
            icon: Delete,
          },
        ]
      },
    },
    [trainingMembershipId]
  )

  return (
    <>
      <Text.Normal weight="semibold" color="textPrimary" size="400">
        {i18n.t('trainings.trainings_revamp.training_detail.employees_table.certificates')}
      </Text.Normal>
      <MaterialsList
        source={source}
        emptyStates={{
          'no-data': {
            title: i18n.t(
              'trainings.trainings_revamp.training_detail.employees_table.cerificates_employee_empty_state.title'
            ),
            description: i18n.t(
              'trainings.trainings_revamp.training_detail.employees_table.cerificates_employee_empty_state.description'
            ),
          },
        }}
      />
    </>
  )
}

export default EmployeesSidepanel
